// @flow
import { useCallback, useMemo } from 'react';
import { useFormStateContext } from 'contexts/FormStateContext';
import { useSectionsProgressContext } from 'contexts/SectionsProgressContext';
import { getStepsToCleanup } from 'hooks/sectionsProgress/utils';
import type { SectionNames } from 'constants/sectionNames';
import {
  getNewFormState,
  getNewFormValues,
  buildMap,
} from 'hooks/affordaPortalFormState/utils';
import useEnableFormStateLogging from 'hooks/affordaPortalFormState/useEnableFormStateLogging';
import { STEP_NAME_TO_SECTION_FIELD_NAME_MAP } from 'hooks/affordaPortalFormState/constants';
import debugLogger from 'utils/logger';

const useFormStateManager = (sectionName: SectionNames) => {
  const { isFormStateLoggingEnabled } = useEnableFormStateLogging();

  const { data, update } = useFormStateContext();

  // Get section progress context for cleanup of unrequired forms
  const {
    currentSectionProgress: { steps, name },
  } = useSectionsProgressContext();

  const logger = useCallback(
    (message, data, status) => {
      if (!isFormStateLoggingEnabled) return;
      debugLogger('Form Manager', message, data, status);
    },
    [isFormStateLoggingEnabled],
  );

  // Calculate section optional field name map for cleanup
  const sectionOptionalFieldNameMap = useMemo(() => {
    if (!steps?.length) return {};
    const sectionFieldNameMap = STEP_NAME_TO_SECTION_FIELD_NAME_MAP[name];
    return buildMap(steps, sectionFieldNameMap);
  }, [name, steps]);

  const { formState } = useMemo(() => {
    const { formState, formValues } = data[sectionName] || {};

    logger(`[${sectionName}] Initial State`, { formState, formValues });

    return {
      formState,
      formValues,
    };
  }, [data, logger, sectionName]);

  const setSectionData = useCallback(
    (
      callback: Object | ((prevState: Object) => Object),
      { control, fields }: { control: Object, fields: Array } | undefined = {},
    ) => {
      update((prevState) => {
        let newState = {};
        const sectionData = prevState[sectionName];

        if (typeof callback === 'function') {
          newState = callback(sectionData.formState);
        } else {
          newState = callback;
        }

        // Calculate steps to cleanup for unrequired forms

        const stepsToCleanup = getStepsToCleanup(steps);
        logger('Steps to cleanup', stepsToCleanup);

        const newFormState = getNewFormState({
          fields,
          newState,
          prevState: sectionData.formState,
        });
        logger('New Form State', newFormState);

        const newFormValues = getNewFormValues({
          control,
          fields,
          prevValues: sectionData.formValues,
          newFormState,
          stepsToCleanup,
          fieldNamesMap: sectionOptionalFieldNameMap,
          sectionName,
          logger,
        });
        logger('New Form Values', newFormValues);

        const payload = {
          ...Object.fromEntries(
            Object.entries(prevState).filter(([key]) => key !== sectionName),
          ),
          [sectionName]: {
            formState: newFormState,
            formValues: newFormValues,
          },
        };

        logger('New State', payload);

        return payload;
      });
    },
    [update, sectionName, steps, logger, sectionOptionalFieldNameMap],
  );

  return {
    formState,
    updateForm: setSectionData,
  };
};

export default useFormStateManager;
